import React, { useState } from "react";
import { View, ScrollView } from "react-native";
import { Building2, Snowflake } from "lucide-react-native";
import { useFertilityTreatments } from "../FertilityTreatmentsContext";
import { getNextTreatmentScreen } from "../getNextTreatmentScreen";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import {
  CounterInput,
  FertilityTreatmentCycleCard,
  MedicalHistoryTextField,
  QuestionLayout,
} from "@src/packages/medical-history/components";
import { MoonDateField } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import type { FertilityTreatmentsStackParamList } from "../FertilityTreatmentsNavigator";
import {
  hasHistoricalTreatmentDetails,
  parseTreatmentNotes,
} from "../transformFertilityTreatmentsState";

type Cycle = {
  id?: string;
  date: string;
  clinic: string;
  embryosFrozen: string;
  notes: string;
};

function emptyCycle(): Cycle {
  return { date: "", clinic: "", embryosFrozen: "", notes: "" };
}

function parseEmbryoCount(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const count = Number(value);
  return Number.isSafeInteger(count) ? count : null;
}

export function EmbryoFreezingScreen() {
  const t = useTranslations();
  const { state, updateState } = useFertilityTreatments();
  const navigation =
    useNavigation<NavigationProp<FertilityTreatmentsStackParamList>>();

  const [count, setCount] = useState<number>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === "embryo_freezing",
    );
    return existing.length > 0 ? existing.length : 1;
  });

  const [cycles, setCycles] = useState<Cycle[]>(() => {
    const existing = (state.treatments || []).filter(
      (treatment) => treatment.type === "embryo_freezing",
    );
    if (existing.length === 0) return [emptyCycle()];
    return existing.map((treatment) => {
      const parsedNotes = parseTreatmentNotes(treatment.notes);
      const embryosFrozen =
        treatment.embryosFrozen !== undefined
          ? treatment.embryosFrozen
          : parseEmbryoCount(parsedNotes.embryosFrozen ?? "");

      return {
        id: treatment.id,
        date: treatment.date ?? "",
        clinic: treatment.provider ?? parsedNotes.clinic ?? "",
        embryosFrozen:
          embryosFrozen !== null && Number.isInteger(embryosFrozen)
            ? String(embryosFrozen)
            : "",
        notes: parsedNotes.remainingNotes ?? "",
      };
    });
  });

  const ensureLength = (nextCount: number) => {
    setCycles((prev) => {
      const arr = [...prev];
      while (arr.length < nextCount) {
        arr.push(emptyCycle());
      }
      if (arr.length > nextCount) arr.length = nextCount;
      return arr;
    });
  };

  const onNext = () => {
    const otherTreatments = state.treatments.filter(
      (treatment) => treatment.type !== "embryo_freezing",
    );
    const nextTreatments = [
      ...otherTreatments,
      ...cycles.map((c) => ({
        ...(c.id ? { id: c.id } : {}),
        type: "embryo_freezing",
        name: null,
        date: c.date || null,
        clinic: c.clinic.trim() || null,
        embryosFrozen: parseEmbryoCount(c.embryosFrozen),
        notes: c.notes.trim() || null,
      })),
    ];
    updateState({
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    const next = getNextTreatmentScreen({
      ...state,
      treatments: nextTreatments,
      currentNavigationIndex: (state.currentNavigationIndex ?? 0) + 1,
    });
    navigation.navigate(next as keyof FertilityTreatmentsStackParamList);
  };

  return (
    <QuestionLayout
      title={t("med_history.fertility.treatments.embryo_freezing.title")}
      subtitle={t("med_history.fertility.treatments.common.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={onNext}
      saveLabel={t("med_history.fertility.continue")}
    >
      <ScrollView contentContainerStyle={{ gap: 16, paddingBottom: 24 }}>
        <CounterInput
          testID="medical-history-treatment-cycle-counter"
          label={t(
            "med_history.fertility.treatments.embryo_freezing.counter_label",
          )}
          description={t(
            "med_history.fertility.treatments.embryo_freezing.counter_description",
          )}
          value={count}
          min={1}
          onIncrement={() => {
            const nextCount = count + 1;
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          onDecrement={() => {
            const nextCount = Math.max(1, count - 1);
            setCount(nextCount);
            ensureLength(nextCount);
          }}
          decrementConfirmation={{
            shouldConfirm: () =>
              hasHistoricalTreatmentDetails(cycles[count - 1] ?? {}),
            title: t(
              "med_history.fertility.treatments.common.remove_cycle_title",
            ),
            message: t(
              "med_history.fertility.treatments.common.remove_cycle_message",
            ),
            cancelLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_cancel",
            ),
            confirmLabel: t(
              "med_history.fertility.treatments.common.remove_cycle_confirm",
            ),
          }}
        />

        {cycles.map((c, i) => (
          <FertilityTreatmentCycleCard
            key={i}
            testID={`medical-history-treatment-cycle-${i + 1}`}
            cycleLabel={t("med_history.fertility.treatments.common.cycle", {
              params: { n: String(i + 1) },
            })}
          >
            <View>
              <MoonDateField
                label={t(
                  "med_history.fertility.treatments.embryo_freezing.date_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.common.date_placeholder",
                )}
                value={c.date}
                onChange={(iso) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, date: iso };
                    return arr;
                  })
                }
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t(
                  "med_history.fertility.treatments.embryo_freezing.clinic_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.common.clinic_placeholder",
                )}
                icon={Building2}
                value={c.clinic || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, clinic: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
            <View>
              <MedicalHistoryTextField
                label={t(
                  "med_history.fertility.treatments.embryo_freezing.embryos_frozen_label",
                )}
                placeholder={t(
                  "med_history.fertility.treatments.embryo_freezing.embryos_frozen_placeholder",
                )}
                icon={Snowflake}
                keyboardType="numeric"
                value={c.embryosFrozen || ""}
                onChangeText={(text) =>
                  setCycles((prev) => {
                    const arr = [...prev];
                    arr[i] = { ...arr[i]!, embryosFrozen: text };
                    return arr;
                  })
                }
                style={{ width: "100%" }}
              />
            </View>
          </FertilityTreatmentCycleCard>
        ))}
      </ScrollView>
    </QuestionLayout>
  );
}

export default EmbryoFreezingScreen;
